<template>
  <div
    class="flex flex-col bg-light-grey rounded-xl p-5 shadow-lg border border-gray-100"
    :class="{'h-3/6 overflow-hidden': isInActiveCall}"
  >
    <p class="text-primary-900 mb-4 text-lg">
      {{ $t("calls.reasons.title") }}
    </p>
    <p v-if="hasReasons" class="text-primary-900 text-sm mb-2">
      {{ $t("calls.reasons.pick_reason") }}
      <span v-if="selectedReasons.length > 0" class="text-green-600 font-medium ml-2">
        ({{ $t("calls.reasons.reason_changed") }})
      </span>
    </p>

    <!-- Nothing to pick from: keep the card in place with an explanation
         rather than rendering a dropdown with no options. -->
    <div
      v-if="!hasReasons"
      class="flex flex-1 flex-col items-center justify-center gap-4 text-center"
    >
      <div class="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center">
        <v-icon size="32" class="text-primary-400">mdi-tag-off-outline</v-icon>
      </div>
      <div>
        <p class="text-primary-900 font-medium mb-1">
          {{ $t("calls.reasons.no_reasons") }}
        </p>
        <p class="text-primary-500 text-sm">
          {{ $t("calls.reasons.no_reasons_description") }}
        </p>
      </div>
    </div>

    <!-- Reasons selection area -->
    <div
      v-else
      class="flex flex-col gap-3"
      :class="{'flex-1 overflow-y-auto': isInActiveCall}"
    >
      <div class="flex items-center gap-2" :class="{'pr-2': isInActiveCall}">
        <ReasonSelectionManager
          ref="reasonSelectionManager"
          :initial-reasons="callReasons"
          v-model="selectedReason"
          :initialModelValue="selectedReason"
        />
      </div>
      
      <!-- Save button - positioned differently based on context -->
      <div 
        class="flex"
        :class="isInActiveCall ? 'justify-end mt-auto pt-2' : 'justify-end'"
      >
        <Button 
          :disabled="!selectedReason || setReasonLoading"
          :loading="setReasonLoading"
          @click="SetCallReason"
          icon="pi pi-check"
          :label="setReasonLoading ? $t('calls.reasons.saving') : $t('calls.reasons.save_reason')"
          size="small"
          class="bg-primary-600 text-white hover:bg-primary-700 disabled:bg-gray-300 disabled:text-gray-500 px-4 py-2 rounded-lg shadow-md"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from "vuex";
import ReasonSelectionManager from "./ReasonSelectionManager.vue";
import { Button } from "primevue";

export default {
  components: {
    ReasonSelectionManager,
    Button,
  },
  props: {
    call: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      selectedReasons: [],
      setReasonLoading: false,
      selectedReason: null,
    };
  },
  methods: {
    ...mapActions([
      "GetCallerLastReasons",
      "AddReasonsToCall",
      "UpdateReasonsToCall",
      "SetMessageDialog",
    ]),
    ...mapMutations(["setACWStatus"]),
    handleACWStatus() {
      if (
        !this.acwSettings?.enabled ||
        !this.acwSettings?.skills?.includes(this.currentCall.Dst) ||
        !this.acwSettings?.after_work?.includes(this.acwReason)
      ) {
        return;
      }
      if (this.acwStatus) {
        this.setACWStatus({
          ...this.acwStatus,
          isSet: this.acwStatus?.isSet?.push(this.acwReason),
        });
      } else {
        this.setACWStatus({
          channel: this.currentCall.Channel,
          isSet: [this.acwReason],
        });
      }
    },
    async SetCallReason() {
      if (
        !this.selectedReason ||
        this.selectedReasons.find((r) => r.id === this.selectedReason.id)
      ) {
        return;
      }

      this.setReasonLoading = true;
      try {
        // Get the full path from root to leaf
        const reasonManager = this.$refs.reasonSelectionManager;
        let fullPath = reasonManager?.selectedPath || [];
        
        // Ensure the leaf node is included in the path
        // If the last item in the path is not the selected reason, add it
        if (fullPath.length > 0 && fullPath[fullPath.length - 1]?.id !== this.selectedReason?.id) {
          fullPath = [...fullPath, this.selectedReason];
        } else if (fullPath.length === 0 && this.selectedReason) {
          // If path is empty but we have a selected reason, use just the reason
          fullPath = [this.selectedReason];
        }
        
        // Concatenate all labels from root to leaf
        const concatenatedLabel = fullPath.map(item => item.name).join(' - ');
        
        // Create a modified reason object with the concatenated label
        const reasonWithFullPath = {
          ...this.selectedReason,
          name: concatenatedLabel || this.selectedReason.name
        };
        
        let payload = {
          callerId: this.call ? this.call.number : this.currentCall?.SourceID,
          channel: this.call ? this.call.dstchannel : this.currentCall?.Channel,
          callid: this.call ? this.call.call_id : this.currentCall?.Linkedid,
          reasons: [reasonWithFullPath],
          satisfaction: 0,
        };
        // Use UpdateReasonsToCall if editing (reasons already set), otherwise AddReasonsToCall
        var resp = this.selectedReasons.length > 0 
          ? await this.UpdateReasonsToCall(payload)
          : await this.AddReasonsToCall(payload);
        this.setReasonLoading = false;
        if (!resp.success) {
          this.SetMessageDialog({ show: true, message: resp.message });
        } else {
          this.SetMessageDialog({
            show: true,
            message: resp.message,
            success: resp.success,
          });
          // this.handleACWStatus();
          this.selectedReasons.push(this.selectedReason);
          // Reset selection after successful save to allow adding more reasons
          this.selectedReason = null;
        }
      } catch (err) {
        console.log("My reps err: ", err);
        let message = err.message;
        if (err.response) {
          message = err.response.data.message;
        }
        this.setReasonLoading = false;
        this.SetMessageDialog({ show: true, message: message });
      }
    },
  },
  // Removed automatic reason saving watcher - now using manual save button
  computed: {
    ...mapGetters([
      "callReasons",
      "acwSettings",
      "acwStatus",
      "currentCall",
      "acwReason",
      "inCall",
    ]),
    // The selector filters out disabled reasons, so an all-disabled tree is
    // just as empty as no tree at all.
    hasReasons() {
      return this.callReasons?.some((reason) => reason.enabled) ?? false;
    },
    // Detect if we're in the active call page context
    isInActiveCall() {
      // Check if we're in an active call or if the parent route indicates active call context
      return this.$route?.name === 'ActiveCall' || this.$route?.path?.includes('/call/active') || this.inCall;
    },
  },
};
</script>
<style scoped></style>
